/**
 * Store helpers — pure utility functions used by store actions.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { MultiAccountStateV4 } from '../../accounts';
import { getActiveAccount } from '../../accounts';
import { GroupId, GROUPS } from '../../config/systemConfig';
import { debug } from '../../utils/logger';

// ── Derived State Utilities ──────────────────────────────────────────

/**
 * Derive selectedGroup from context instead of relying on manual sync.
 *
 * Werkelijke prioriteit (gecorrigeerd 12 aug 2026):
 *   1. actieve leerling → eigen `grade`
 *   2. volwassene die een leerling bekijkt → `grade` van die leerling
 *   3. anders → de opgeslagen `selectedGroup` (persist-waarde of expliciete
 *      override via `setSelectedGroup`)
 *
 * LET OP: de JSDoc noemde hier tot 12 aug 2026 een vierde stap
 * `classroom.groupLevel`. Die tak is **nooit geïmplementeerd**. Dat is meer dan
 * een schoonheidsfout: een ouder of leerkracht die géén leerling heeft
 * geselecteerd, valt niet terug op de groep van zijn klas maar op de laatst
 * opgeslagen waarde — die van een ánder account kan zijn. Wie op de docstring
 * vertrouwde, nam een klas-fallback aan die er niet is.
 *
 * Wil je die tak wél, bouw hem dan hier en niet in een aanroeper: dit is de
 * enige leesbron voor de actieve groep.
 *
 * Usage: const group = deriveSelectedGroup(store.getState())
 */
export function deriveSelectedGroup(state: {
  multiAccountState: MultiAccountStateV4 | null;
  viewingLearnerId: string | null;
  selectedGroup: GroupId;
}): GroupId {
  const mas = state.multiAccountState;
  if (!mas) return state.selectedGroup;
  const activeAccount = getActiveAccount(mas.accounts);
  if (!activeAccount) return state.selectedGroup;

  // 1. Active learner → use their grade
  if (activeAccount.role === 'learner' && 'grade' in activeAccount) {
    const grade = (activeAccount as {
      grade?: number;
    }).grade;
    if (grade && GROUPS.includes(grade as GroupId)) {
      return grade as GroupId;
    }
  }

  // 2. Viewing a learner (teacher/parent) → use that learner's grade
  if (state.viewingLearnerId) {
    const viewedAccount = mas.accounts.accountsById[state.viewingLearnerId];
    if (viewedAccount?.role === 'learner' && 'grade' in viewedAccount) {
      const grade = (viewedAccount as {
        grade?: number;
      }).grade;
      if (grade && GROUPS.includes(grade as GroupId)) {
        return grade as GroupId;
      }
    }
  }

  // 3. Fall back to current selectedGroup (which may be manually set or synced)
  return state.selectedGroup;
}

// ── Helper: Remove demo accounts ─────────────────────────────────────
//
// De demo-laag is verwijderd (aug 2026), maar deze functie blijft — en draait
// nu bij ELKE boot in `initialize`. Reden: het weghalen van de generator
// verwijdert geen data die al in een browser staat. Zonder deze pass houdt
// iedereen die de demo ooit heeft geladen negen verzonnen accounts, die in
// het adminportaal niet van echte te onderscheiden zijn.
//
// `isDemo` op het accounttype blijft daarom ook bestaan: het is het enige
// kenmerk waaraan die achterblijvers te herkennen zijn. Niets zet het veld
// nog op `true`.

export function removeDemoAccounts(state: MultiAccountStateV4): MultiAccountStateV4 {
  const demoIds = new Set(Object.values(state.accounts.accountsById).filter(a => a.isDemo).map(a => a.id));
  if (demoIds.size === 0) return state;
  const realAccounts = Object.fromEntries(Object.entries(state.accounts.accountsById).filter(([id]) => !demoIds.has(id)));
  const realLearnerData = Object.fromEntries(Object.entries(state.learnerData).filter(([id]) => !demoIds.has(id)));
  const filterIds = (ids: string[]) => ids.filter(id => !demoIds.has(id));
  const cleanRelationships = {
    parentToLearners: Object.fromEntries(Object.entries(state.accounts.relationships.parentToLearners).filter(([parentId]) => !demoIds.has(parentId)).map(([parentId, learnerIds]) => [parentId, filterIds(learnerIds)]).filter(([, ids]) => (ids as string[]).length > 0)),
    learnerToParents: Object.fromEntries(Object.entries(state.accounts.relationships.learnerToParents).filter(([learnerId]) => !demoIds.has(learnerId)).map(([learnerId, parentIds]) => [learnerId, filterIds(parentIds)]).filter(([, ids]) => (ids as string[]).length > 0)),
    teacherToLearners: Object.fromEntries(Object.entries(state.accounts.relationships.teacherToLearners).filter(([teacherId]) => !demoIds.has(teacherId)).map(([teacherId, learnerIds]) => [teacherId, filterIds(learnerIds)]).filter(([, ids]) => (ids as string[]).length > 0)),
    learnerToTeachers: Object.fromEntries(Object.entries(state.accounts.relationships.learnerToTeachers ?? {}).filter(([learnerId]) => !demoIds.has(learnerId)).map(([learnerId, teacherIds]) => [learnerId, filterIds(teacherIds)]).filter(([, ids]) => (ids as string[]).length > 0))
  };
  debug(`🧹 Cleaned up ${demoIds.size} demo accounts`);
  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById: realAccounts,
      relationships: cleanRelationships
    },
    learnerData: realLearnerData
  };
}